import React, { Fragment, useState, useCallback, useContext, useMemo, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import cx from 'classnames'
// form validation
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
// assets
import Pencil from 'Assets/images/pencil.svg'
import NeedMoreInfoIcon from 'Images/ml_need_more_information.svg'
import EscalateToNextStepIcon from 'Images/ml_escalate_to_next_step.svg'
import OnSuccessIcon from 'Images/ml_on_success.svg'
import Settings from 'Images/bot-flow-settings.svg'
import ForwardArrowIcon from 'Images/ml_forward-arrow-green.svg'
// components
import MLSearch from './MlSearchModal'
// contexts & store
import { deleteAction, setFlowHistory, updateAction, updateBotResponseSettings } from 'Actions/flow'
import { StepContext, useFlowContext } from 'Src/containers/contexts'

import {
  getEscalateStep,
  getEndConversationStep,
  getDefaultBotSettings,
  getMlActionPayload
} from './bot-response-setting-helper'

import { findgoToStep } from '../../utils/findGoToStep'
import { useGetAllKbSectionsQuery } from 'Src/b/pages/protected/Bots/bot-flows.service'
import { getAccountDetails } from 'Selectors/account'
import toast from 'react-hot-toast'

const MlCollectUserQuestion = ({ actionId, name = 'collectUserQuestion' }) => {
  // state
  const [showMlSearchEdit, setShowMlSearchEdit] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [hover, setHover] = useState(false)
  const [selectedMlStep, setSelectedMlStep] = useState()
  // context & store
  const dispatch = useDispatch()
  const { step, stepId, row } = useContext(StepContext)
  const { steps, isNewUI } = useFlowContext()

  const accountData = useSelector(getAccountDetails)

  const settings = _.get(step, 'settings', {})
  const action = _.get(settings, `${actionId}`, {})
  const defaultPlaceholder = 'Type your question here...'

  const escalateStep = getEscalateStep({ ...steps })
  const endConversationStep = getEndConversationStep({ ...steps })
  const botSettingConfig = useMemo(
    () => getDefaultBotSettings({ ...action }, escalateStep, stepId, false, accountData),
    [action, escalateStep, stepId, accountData]
  )

  const [mlConfig, setMlConfig] = useState(botSettingConfig)

  const {
    data: allKBSections,
    isLoading: isGetAllKBSectionsLoading,
    isError: isGetAllKBSectionsError,
    error: getAllKBSectionsError
  } = useGetAllKbSectionsQuery()

  const {
    reset,
    trigger,
    setValue,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      yup
        .object({
          question_placeholder: yup
            .string()
            .required('This field is required')
            .max(100, 'This field can contain maximum of ${max} characters')
        })
        .required()
    ),
    defaultValues: { question_placeholder: action.question_placeholder }
  })

  const onSaveClick = useCallback(() => {
    if (!_.isEmpty(errors)) {
      return
    }

    const payload = getMlActionPayload({ ...mlConfig }, escalateStep, stepId, actionId, accountData)
    const botResponseSettings = _.omit({ ...payload.setting }, [
      'type',
      'collect_feedback',
      'enable_kb_filter',
      'enable_tags_filter',
      'selected_sections',
      'selected_tags'
    ])
    dispatch(updateAction({ ...payload }))
    dispatch(updateBotResponseSettings(botResponseSettings))
    setShowEdit(false)
  }, [actionId, dispatch, errors, escalateStep, mlConfig, stepId, accountData])

  const onDeleteClick = useCallback(() => {
    dispatch(
      deleteAction({
        stepId,
        actionId
      })
    )
  }, [dispatch, actionId, stepId])

  const onEditMlPlaceholder = useCallback(() => {
    setShowEdit(true)
  }, [])

  const onCloseEdit = () => {
    const question_placeholder = action.question_placeholder ? action.question_placeholder : defaultPlaceholder
    setShowEdit(false)
    reset({
      question_placeholder
    })
    setMlConfig({ ...mlConfig, question_placeholder })
  }

  const onClickMLNextStep = (key) => {
    const defaultValues = { ...mlConfig }
    setSelectedMlStep(key)
    const goToStep = findgoToStep(defaultValues[key])

    if (goToStep) {
      dispatch(setFlowHistory({ goToStep, row }))
    } else if (!goToStep && defaultValues[key].type === 'endChat') {
      if (endConversationStep && endConversationStep.step_id) {
        dispatch(setFlowHistory({ goToStep: endConversationStep.step_id, row }))
      } else {
        dispatch(setFlowHistory({ goToStep: stepId, row: row - 1 }))
      }
    }
  }

  const showGetAllKBSectionsErrorToast = useCallback(() => {
    toast.error(getAllKBSectionsError.data?.message)
  }, [getAllKBSectionsError])

  useEffect(() => {
    if (showMlSearchEdit && isGetAllKBSectionsError) {
      showGetAllKBSectionsErrorToast()
      setShowMlSearchEdit(false)
    }
  }, [isGetAllKBSectionsError, showMlSearchEdit, showGetAllKBSectionsErrorToast, setShowMlSearchEdit])

  const onClickBotResponseSettings = () => {
    if (isGetAllKBSectionsError) {
      showGetAllKBSectionsErrorToast()
      return
    }
    setShowMlSearchEdit(true)
    setShowEdit(false)
  }

  return (
    <div className={isNewUI ? 'ml-search-container new-design' : 'ml-search-container'}>
      <form onSubmit={handleSubmit(onSaveClick)}>
        <div className='ml-content-wrapper' onMouseOver={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <div className='ml-search-text'> Collect User Question </div>
          {showEdit && <div className='ml-search-subtext'> Customize question prompt </div>}
          <div className='ml-search-textarea-wrapper' onClick={() => setShowEdit(true)}>
            <Fragment>
              <Controller
                control={control}
                shouldUnregister
                name='question_placeholder'
                rules={{
                  required: {
                    value: true,
                    message: 'This field is required'
                  }
                }}
                render={({ field: { value, onChange }, formState: { errors } }) => {
                  return (
                    <Fragment>
                      {!showEdit && <div className='ml-search-textarea'>{value}</div>}
                      {showEdit && (
                        <input
                          autoFocus
                          id='questionPlaceholder'
                          type='text'
                          className={cx('form-control', 'input-text', { 'is-invalid': errors.question_placeholder })}
                          value={value}
                          onBlur={(e) => {
                            const value = e.target.value?.trim() ?? ''
                            setValue('question_placeholder', value)
                            setMlConfig({ ...mlConfig, question_placeholder: value })
                            trigger('question_placeholder')
                          }}
                          onChange={(e) => onChange(e.target.value)}
                          placeholder='Eg. "Type your question here..."'
                        />
                      )}
                      {errors.question_placeholder && (
                        <small className='form-text text-danger'>{errors.question_placeholder?.message}</small>
                      )}
                    </Fragment>
                  )
                }}
              />
            </Fragment>
            {errors.placeholder && <small className='form-text text-danger'>{errors.placeholder.message}</small>}
            {!showEdit && hover && (
              <div className='actions'>
                <div className='action-icon edit' title='edit' onClick={() => onEditMlPlaceholder()}>
                  <Pencil height='13' width='13' />
                </div>
              </div>
            )}
          </div>
          {showEdit && (
            <div className='action-group'>
              <div className='primary-action'>
                <button className='button-primary small save' type='submit'>
                  Save
                </button>
                <button type='button' className='btn cancel' onClick={() => onCloseEdit()}>
                  Cancel
                </button>
              </div>
              <button type='button' className='btn delete' onClick={onDeleteClick}>
                Delete
              </button>
            </div>
          )}
        </div>
        <div className='bot-response-settings-wrapper'>
          <div className='bot-response-settings-content' onClick={() => onClickBotResponseSettings()}>
            <div className='text'> Bot Response Settings </div>
            <div className='icon'>
              <Settings className='settings-icon' height='13' width='13' />
            </div>
          </div>
          <div
            className={cx('ml-next-step', selectedMlStep && selectedMlStep === 'helpfulAction' ? 'active' : '')}
            onClick={() => onClickMLNextStep('helpfulAction')}
          >
            <OnSuccessIcon className='circle-icon' />
            <div className='text'> On Success </div>
            <div className='arrow-wrapper'>
              <ForwardArrowIcon width='16' height='16' className='arrow-icon' />
            </div>
          </div>
          <div
            className={cx('ml-next-step', selectedMlStep && selectedMlStep === 'notHelpfulAction' ? 'active' : '')}
            onClick={() => onClickMLNextStep('notHelpfulAction')}
          >
            <EscalateToNextStepIcon className='circle-icon' />
            <div className='text'> Escalate to Next Step </div>
            <div className='arrow-wrapper'>
              <ForwardArrowIcon width='16' height='16' className='arrow-icon' />
            </div>
          </div>
          <div
            className={cx('ml-next-step', selectedMlStep && selectedMlStep === 'noResultAction' ? 'active' : '')}
            onClick={() => onClickMLNextStep('noResultAction')}
          >
            <NeedMoreInfoIcon className='circle-icon' />
            <div className='text'> Need More Information </div>
            <div className='arrow-wrapper'>
              <ForwardArrowIcon width='16' height='16' className='arrow-icon' />
            </div>
          </div>
        </div>
      </form>
      {showMlSearchEdit && (
        <MLSearch
          isOpen={showMlSearchEdit}
          setMlConfig={(e) => setMlConfig(e)}
          showLoader={isGetAllKBSectionsLoading}
          allKBSections={allKBSections}
          actionId={actionId}
          isNewUI={isNewUI}
          clearStepSelection={() => setSelectedMlStep(null)}
          mode={actionId ? 'edit' : 'new'}
          onCloseModal={(e) => setShowMlSearchEdit(e)}
        />
      )}
    </div>
  )
}

export default MlCollectUserQuestion
